import React from 'react';
let gradientSeed = 0;
type IsotypeProps = {
  className?: string;
  /** Renders the mark inside the official dark circular badge */
  badge?: boolean;
  /** Single-ink version: the whole mark uses currentColor */
  monochrome?: boolean;
};

/**
 * Official Creativo Digital isotype: interlocking C + D capsule with the
 * ascending growth bars, per the brand identity guide.
 */
export function CDIsotype({
  className = '',
  badge = false,
  monochrome = false
}: IsotypeProps) {
  const id = React.useMemo(() => `cd-${++gradientSeed}`, []);
  const gradient = `url(#${id}-grad)`;
  const mark = <>
      <g mask={`url(#${id}-ring)`} data-id="element-23" data-mp="1chervekvkgjq">
        <rect x="0" y="0" width="64" height="64" fill={monochrome ? 'currentColor' : '#FFFFFF'} data-id="element-24" data-mp="1cgdidejic244" />
        <path d="M37 -2 L70 -2 L70 66 L27 66 Z" fill={monochrome ? 'currentColor' : gradient} data-id="element-25" data-mp="24yr62lb4q7ph" />
      </g>
      <g fill={monochrome ? 'currentColor' : gradient} data-id="element-26" data-mp="2067uftw05mnb">
        <rect x="29.5" y="33.5" width="3" height="6.5" rx="1.5" data-id="element-27" data-mp="13unt96fhm2h5" />
        <rect x="35" y="30" width="3" height="10" rx="1.5" data-id="element-28" data-mp="13xjqesjign4j" />
        <rect x="40.5" y="26.5" width="3" height="13.5" rx="1.5" data-id="element-29" data-mp="18sz2pg1veayu" />
      </g>
    </>;
  return <svg viewBox="0 0 64 64" className={className} role="presentation" aria-hidden="true" data-id="element-30" data-mp="u4l1f9k3quke">
      <defs data-id="element-31" data-mp="z2ml9wpcqxwx">
        <linearGradient id={`${id}-grad`} x1="0" y1="1" x2="1" y2="0" data-id="element-32" data-mp="z2e9k84ytynb">
          <stop offset="0%" stopColor="#2563FF" data-id="element-33" data-mp="l34qn0pdkqfn" />
          <stop offset="100%" stopColor="#8B5CF6" data-id="element-34" data-mp="p61f381ljgri" />
        </linearGradient>
        <mask id={`${id}-ring`} data-id="element-35" data-mp="p46ujuu8lqny">
          <rect x="4" y="13" width="56" height="38" rx="19" fill="#fff" data-id="element-36" data-mp="b71o3tqm2ps6" />
          <rect x="17.5" y="24" width="29" height="16" rx="8" fill="#000" data-id="element-37" data-mp="b8uur9185a06" />
        </mask>
      </defs>
      {badge ? <>
          <circle cx="32" cy="32" r="32" fill="#0D0D0F" data-id="element-38" data-mp="25k9qq7ktw5od" />
          <g transform="translate(32 32) scale(0.86) translate(-32 -32)" data-id="element-39" data-mp="25iq8ibfm3kjy">
            {mark}
          </g>
        </> : mark}
    </svg>;
}
type WordmarkProps = {
  className?: string;
  showTagline?: boolean;
  size?: 'sm' | 'md' | 'lg';
};

/**
 * Official stacked wordmark: CREATIVO in white, DIGITAL in the brand gradient,
 * with the optional discipline tagline.
 */
export function BrandWordmark({
  className = '',
  showTagline = false,
  size = 'sm'
}: WordmarkProps) {
  const scale = {
    sm: {
      main: 'text-[13px] tracking-[0.30em]',
      sub: 'text-[8.5px] tracking-[0.44em]',
      tag: 'text-[6px] tracking-[0.24em]'
    },
    md: {
      main: 'text-[17px] tracking-[0.32em]',
      sub: 'text-[11px] tracking-[0.46em]',
      tag: 'text-[7.5px] tracking-[0.26em]'
    },
    lg: {
      main: 'text-[22px] tracking-[0.34em]',
      sub: 'text-[14px] tracking-[0.48em]',
      tag: 'text-[9px] tracking-[0.28em]'
    }
  }[size];
  return <span className={`flex flex-col leading-none ${className}`} data-id="element-40" data-mp="3alj27kcitw96">
      <span className={`font-semibold text-white ${scale.main}`} data-id="element-41" data-mp="35ry0bh7t4i4p">CREATIVO</span>
      <span className={`brand-gradient-text mt-1 font-medium ${scale.sub}`} data-id="element-42" data-mp="3e25eoy9i1ju6">
        DIGITAL
      </span>
      {showTagline && <span className={`mt-1.5 font-light text-white/45 ${scale.tag}`} data-id="element-43" data-mp="3i3h53607693f">
          MARKETING | CONTENIDO | ESTRATEGIA
        </span>}
    </span>;
}